package sarka.design.skoria.network

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*
import kotlinx.serialization.Serializable

@Serializable
data class LoginRequest(
    val username: String,
    val password: String
)

@Serializable
data class RegisterRequest(
    val username: String,
    val email: String,
    val password: String
)

@Serializable
data class VerifyEmailRequest(
    val username: String,
    val code: String
)

@Serializable
data class ResendCodeRequest(
    val username: String
)

@Serializable
data class AuthResponse(
    val token: String,
    val username: String,
    val email: String,
    val isEmailVerified: Boolean
)

@Serializable
data class RegisterResponse(
    val message: String,
    val username: String,
    val email: String
)

@Serializable
data class ErrorResponse(
    val message: String
)

sealed class AuthResult {
    data class Success(val response: AuthResponse) : AuthResult()
    data class Error(val message: String) : AuthResult()
}

sealed class RegisterResult {
    data class Success(val username: String, val email: String) : RegisterResult()
    data class Error(val message: String) : RegisterResult()
}

class AuthApi(
    private val baseUrl: String = defaultApiUrl(),
    private val httpClient: HttpClient = createPlatformHttpClient()
) {

    suspend fun login(username: String, password: String): AuthResult {
        return try {
            val response = httpClient.post("$baseUrl/auth/login") {
                contentType(ContentType.Application.Json)
                setBody(LoginRequest(username, password))
            }
            if (response.status == HttpStatusCode.OK) {
                AuthResult.Success(response.body<AuthResponse>())
            } else {
                val error = try {
                    response.body<ErrorResponse>()
                } catch (_: Throwable) {
                    ErrorResponse("Login failed")
                }
                AuthResult.Error(error.message)
            }
        } catch (e: Throwable) {
            val message = when {
                e.message?.contains("fetch", ignoreCase = true) == true -> "Could not reach the server."
                else -> e.message ?: "Network error"
            }
            AuthResult.Error(message)
        }
    }

    suspend fun register(username: String, email: String, password: String): RegisterResult {
        return try {
            val response = httpClient.post("$baseUrl/auth/register") {
                contentType(ContentType.Application.Json)
                setBody(RegisterRequest(username, email, password))
            }
            if (response.status == HttpStatusCode.Created) {
                val registerResponse = response.body<RegisterResponse>()
                RegisterResult.Success(registerResponse.username, registerResponse.email)
            } else {
                val error = try {
                    response.body<ErrorResponse>()
                } catch (_: Throwable) {
                    ErrorResponse("Registration failed")
                }
                RegisterResult.Error(error.message)
            }
        } catch (e: Throwable) {
            val message = when {
                e.message?.contains("fetch", ignoreCase = true) == true -> "Could not reach the server."
                else -> e.message ?: "Network error"
            }
            RegisterResult.Error(message)
        }
    }

    suspend fun verifyEmail(username: String, code: String): Result<AuthResponse> {
        return try {
            val response = httpClient.post("$baseUrl/auth/verify-email") {
                contentType(ContentType.Application.Json)
                setBody(VerifyEmailRequest(username, code))
            }
            if (response.status == HttpStatusCode.OK) {
                Result.success(response.body<AuthResponse>())
            } else {
                val error = try {
                    response.body<ErrorResponse>()
                } catch (_: Throwable) {
                    ErrorResponse("Verification failed")
                }
                Result.failure(Exception(error.message))
            }
        } catch (e: Throwable) {
            Result.failure(e)
        }
    }

    suspend fun resendCode(username: String): Result<String> {
        return try {
            val response = httpClient.post("$baseUrl/auth/resend-code") {
                contentType(ContentType.Application.Json)
                setBody(ResendCodeRequest(username))
            }
            if (response.status == HttpStatusCode.OK) {
                Result.success("Verification code sent")
            } else {
                val error = try {
                    response.body<ErrorResponse>()
                } catch (_: Throwable) {
                    ErrorResponse("Failed to resend code")
                }
                Result.failure(Exception(error.message))
            }
        } catch (e: Throwable) {
            Result.failure(e)
        }
    }

    suspend fun getUserEmail(username: String): Result<String> {
        return try {
            val response = httpClient.get("$baseUrl/auth/user-email/$username")
            if (response.status == HttpStatusCode.OK) {
                val body = response.body<Map<String, String>>()
                val email = body["email"] ?: return Result.failure(Exception("Email not found"))
                Result.success(email)
            } else {
                val error = try {
                    response.body<ErrorResponse>()
                } catch (_: Throwable) {
                    ErrorResponse("Failed to get user email")
                }
                Result.failure(Exception(error.message))
            }
        } catch (e: Throwable) {
            Result.failure(e)
        }
    }
}
